import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ACCOUNT_PENDING = '/account-pending';
const ACCOUNT_BLOCKED = '/account-blocked';
const WELCOME = '/welcome';

/** Where non–super-admin users must land when not approved or suspended. */
function accountGatePath(
  isSaasOwner: boolean,
  accountStatus: 'pending' | 'approved' | 'banned' | undefined,
): typeof ACCOUNT_PENDING | typeof ACCOUNT_BLOCKED | null {
  if (isSaasOwner) return null;
  const s = accountStatus ?? 'approved';
  if (s === 'banned') return ACCOUNT_BLOCKED;
  if (s === 'pending') return ACCOUNT_PENDING;
  return null;
}

interface AuthGuardProps {
  children: ReactNode;
  requireWorkspace?: boolean;
  requireSaasOwner?: boolean;
}

export function AuthGuard({ children, requireWorkspace = false, requireSaasOwner = false }: AuthGuardProps) {
  const { isAuthenticated, loading, activeWorkspace, isSaasOwner, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  const gate = accountGatePath(isSaasOwner, user?.accountStatus);
  const path = location.pathname;

  if (gate) {
    if (path !== gate) {
      return <Navigate to={gate} replace />;
    }
  } else if (path === ACCOUNT_PENDING || path === ACCOUNT_BLOCKED) {
    return (
      <Navigate
        to={isSaasOwner ? '/vault' : activeWorkspace ? '/vault' : '/select-workspace'}
        replace
      />
    );
  }

  if (requireSaasOwner && !isSaasOwner) {
    return <Navigate to="/select-workspace" replace />;
  }

  // First-run onboarding: a brand-new user (not onboarded) with a workspace is
  // sent through the welcome wizard once. Super-admins skip it.
  const needsOnboarding = !isSaasOwner && activeWorkspace != null && user?.onboarded === false;
  if (needsOnboarding && path !== WELCOME) {
    return <Navigate to={WELCOME} replace />;
  }
  if (!needsOnboarding && path === WELCOME) {
    return <Navigate to={activeWorkspace ? '/vault' : '/select-workspace'} replace />;
  }

  if (requireWorkspace && !activeWorkspace) {
    return <Navigate to="/select-workspace" replace />;
  }

  return <>{children}</>;
}

/** sessionStorage key: an in-app path to return to after signing in (HDR Studio's "Sign in"). */
export const AUTH_RETURN_KEY = 'auth_return_to';

/** A same-app path ("/x…", never "//host" or "/\\host"), else null. */
function safeReturnPath(p: unknown): string | null {
  return typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/\\') ? p : null;
}

/**
 * Where to go after signing in, if somewhere asked: AuthGuard's `state.from`
 * (a guarded page that bounced to the login) or a path an editor stored
 * before sending the user here (`AUTH_RETURN_KEY`). Pure — StrictMode renders
 * twice — so the stored path is cleared by an effect once signed in.
 */
function readReturnPath(state: unknown): string | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(AUTH_RETURN_KEY);
  } catch {
    /* storage blocked */
  }
  const from = (state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const fromPath = from?.pathname ? `${from.pathname}${from.search ?? ''}` : null;
  return safeReturnPath(stored) ?? safeReturnPath(fromPath);
}

export function GuestGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading, isSaasOwner, activeWorkspace, user } = useAuth();
  const location = useLocation();
  // Signed in: the return path (if any) is being followed now — forget it.
  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      sessionStorage.removeItem(AUTH_RETURN_KEY);
    } catch {
      /* storage blocked */
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  if (isAuthenticated) {
    const gate = accountGatePath(isSaasOwner, user?.accountStatus);
    if (gate) {
      return <Navigate to={gate} replace />;
    }

    const twoFactorReset = sessionStorage.getItem('2fa_reset');
    if (twoFactorReset) {
      return <Navigate to="/settings" replace />;
    }

    const pendingToken = localStorage.getItem('invite_token');
    if (pendingToken) {
      localStorage.removeItem('invite_token');
      return <Navigate to={`/invite/${pendingToken}`} replace />;
    }

    // Back to the page that sent the user to sign in (its own guard re-checks it).
    const back = readReturnPath(location.state);
    if (back && back !== '/') {
      return <Navigate to={back} replace />;
    }

    if (isSaasOwner) {
      return <Navigate to="/home" replace />;
    }
    if (activeWorkspace) {
      return <Navigate to="/home" replace />;
    }
    return <Navigate to="/select-workspace" replace />;
  }

  return <>{children}</>;
}
